/**
 * Admin Moderation - Reports Tab
 * View and manage reported listings
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Flag, AlertTriangle, Check, X, Eye, ExternalLink,
  Loader2, RefreshCw, Filter, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAdmin } from './AdminContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const AdminReports = ({ onCountChange }) => {
  const { apiCall, formatDate } = useAdmin();
  
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // pending, resolved, dismissed
  const [selectedReport, setSelectedReport] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  
  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiCall('get', `/api/admin/reported-listings?status=${filter}`);
      const reportsList = data.reports || data || [];
      setReports(reportsList);
      // Update parent count when filter is 'pending'
      if (filter === 'pending' && onCountChange) {
        onCountChange(reportsList.length);
      }
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      setLoading(false);
    }
  }, [apiCall, filter, onCountChange]);
  
  useEffect(() => {
    fetchReports();
  }, [fetchReports]);
  
  const handleResolve = async (reportId, action) => {
    try {
      setActionLoading(reportId);
      await apiCall('put', `/api/admin/reports/${reportId}/${action}`);
      toast.success(action === 'resolve' ? 'Report resolved' : 'Report dismissed');
      // Update count immediately
      if (onCountChange) {
        onCountChange(prev => Math.max(0, prev - 1));
      }
      fetchReports();
    } catch (error) {
      console.error('Failed to update report:', error);
    } finally {
      setActionLoading(null);
      setSelectedReport(null);
    }
  };
  
  const handleDeleteListing = async (listingId, reportId) => {
    try {
      setActionLoading(reportId);
      await apiCall('delete', `/api/admin/listings/${listingId}`);
      // The delete endpoint already resolves pending reports, so we just refresh
      toast.success('Listing deleted and report resolved');
      // Update count immediately
      if (onCountChange) {
        onCountChange(prev => Math.max(0, prev - 1));
      }
      fetchReports();
    } catch (error) {
      console.error('Failed to delete listing:', error);
      toast.error('Failed to delete listing');
    } finally {
      setActionLoading(null);
      setSelectedReport(null);
    }
  };
  
  const getReasonBadge = (reason) => {
    const colors = {
      spam: 'bg-yellow-500/20 text-yellow-600',
      inappropriate: 'bg-red-500/20 text-red-600',
      scam: 'bg-red-500/20 text-red-600',
      duplicate: 'bg-blue-500/20 text-blue-600',
      wrong_category: 'bg-purple-500/20 text-purple-600',
      other: 'bg-gray-500/20 text-gray-600'
    };
    return colors[reason] || colors.other;
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Flag className="w-6 h-6 text-primary" />
            Reported Listings
          </h2>
          <p className="text-sm text-muted-foreground">Review and moderate reported content</p>
        </div>
        <button
          onClick={fetchReports}
          disabled={loading}
          className="p-2 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      
      {/* Filters */}
      <div className="flex gap-2">
        {[
          { id: 'pending', label: 'Pending', count: null },
          { id: 'resolved', label: 'Resolved', count: null },
          { id: 'dismissed', label: 'Dismissed', count: null }
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === f.id
                ? 'bg-primary text-primary-foreground'
                : 'bg-secondary hover:bg-secondary/80'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>
      
      {/* Reports List */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-12">
            <Flag className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">No {filter} reports</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {reports.map(report => (
              <div 
                key={report.id}
                className="p-4 hover:bg-secondary/20 transition-colors"
              >
                <div className="flex items-start gap-4">
                  {/* Listing Image */}
                  <div className="w-20 h-20 rounded-lg bg-secondary overflow-hidden flex-shrink-0">
                    {report.listing?.images?.[0] ? (
                      <img 
                        src={report.listing.images[0]} 
                        alt="" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <AlertTriangle className="w-6 h-6 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  
                  {/* Report Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-medium truncate">
                          {report.listing?.title || 'Deleted Listing'}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Reported by: {report.reporter_name || 'Anonymous'} • {formatDate(report.created_at)}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getReasonBadge(report.reason)}`}>
                        {report.reason?.replace('_', ' ')}
                      </span>
                    </div>
                    
                    {report.description && (
                      <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                        {report.description}
                      </p>
                    )}
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-3">
                      {filter === 'pending' && (
                        <>
                          <button
                            onClick={() => setSelectedReport(report)}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                          >
                            <Eye className="w-3 h-3" />
                            Review
                          </button>
                          <button
                            onClick={() => handleResolve(report.id, 'dismiss')}
                            disabled={actionLoading === report.id}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-secondary"
                          >
                            {actionLoading === report.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <X className="w-3 h-3" />
                            )}
                            Dismiss
                          </button>
                        </>
                      )}
                      {report.listing_id && (
                        <a
                          href={`/listing/${report.listing_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
                        >
                          <ExternalLink className="w-3 h-3" />
                          View Listing
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Review Dialog */}
      <Dialog open={!!selectedReport} onOpenChange={() => setSelectedReport(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="w-5 h-5 text-red-500" />
              Review Report
            </DialogTitle>
            <DialogDescription>
              Take action on this reported listing
            </DialogDescription>
          </DialogHeader>
          
          {selectedReport && (
            <div className="space-y-4">
              <div className="p-4 bg-secondary/30 rounded-lg">
                <h4 className="font-medium">{selectedReport.listing?.title || 'Deleted Listing'}</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Reason: <span className="font-medium">{selectedReport.reason?.replace('_', ' ')}</span>
                </p>
                {selectedReport.description && (
                  <p className="text-sm mt-2">{selectedReport.description}</p>
                )}
              </div>
              
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => handleResolve(selectedReport.id, 'resolve')}
                  disabled={actionLoading === selectedReport.id}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                >
                  {actionLoading === selectedReport.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  Mark as Resolved (Keep Listing)
                </button>
                
                <button
                  onClick={() => handleDeleteListing(selectedReport.listing_id, selectedReport.id)}
                  disabled={actionLoading === selectedReport.id || !selectedReport.listing_id}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
                >
                  {actionLoading === selectedReport.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  Delete Listing & Resolve
                </button>
                
                <button
                  onClick={() => handleResolve(selectedReport.id, 'dismiss')}
                  disabled={actionLoading === selectedReport.id}
                  className="flex items-center justify-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-secondary"
                >
                  <X className="w-4 h-4" />
                  Dismiss Report
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminReports;
